export const javaQAData = [
    {
        category: "Basics and Syntax",
        questions: [
            {
                id: 1,
                q: "What is Java?",
                a: "Java is a high-level, class-based, object-oriented programming language that is designed to have as few implementation dependencies as possible. It is a general-purpose programming language intended to let application developers write once, run anywhere (WORA), meaning that compiled Java code can run on all platforms that support Java without the need for recompilation.",
                concise: "High-level, OOP, WORA (Write Once, Run Anywhere) language."
            },
            {
                id: 2,
                q: "Explain the JDK, JRE, and JVM.",
                a: "<strong>JVM (Java Virtual Machine):</strong> The engine that provides a runtime environment to drive Java applications. It converts Java bytecode into machine language.<br/><strong>JRE (Java Runtime Environment):</strong> A part of software designed to run other software. It contains libraries and other files that JVM uses at runtime.<br/><strong>JDK (Java Development Kit):</strong> A software development environment used for developing Java applications. It contains JRE and development tools.",
                concise: "JVM (Run), JRE (Libraries), JDK (Development Tools)."
            },
            {
                id: 3,
                q: "What are variables in Java?",
                a: "Variables are containers for storing data values. In Java, each variable must be declared with a data type that designates the type and quantity of value it can hold. Java is a statically typed language, meaning variables must be defined before they are used.",
                concise: "Named containers for data values with specific types."
            },
            {
                id: 4,
                q: "What is typecasting in Java?",
                a: "Typecasting is the process of converting a variable from one type to another. <strong>Widening Casting (Implicit):</strong> automatic conversion from a smaller to a larger type (e.g., int to double). <strong>Narrowing Casting (Explicit):</strong> manual conversion from a larger to a smaller type (e.g., double to int).",
                concise: "Converting types: Widening (Implicit/Auto) and Narrowing (Explicit/Manual)."
            },
            {
                id: 5,
                q: "How do you declare an array in Java?",
                a: "An array is a container object that holds a fixed number of values of a single type. Arrays in Java are fixed-size once initialized.",
                concise: "Fixed-size container for same-type elements.",
                javaCode: `int[] numbers = new int[5];\nnumbers[0] = 10;\nnumbers[1] = 20;\n\n// Inline initialization\nString[] fruits = {"Apple", "Banana", "Orange"};`,
                pythonCode: `# Arrays (Lists) in Python are dynamic-sized\nnumbers = [0] * 5\nnumbers[0] = 10\n\n# Inline initialization\nfruits = ["Apple", "Banana", "Orange"]\n# Can easily add elements\nfruits.append("Mango")`
            },
            {
                id: 6,
                q: "Explain the main method in Java.",
                a: "The <code>public static void main(String[] args)</code> method is the entry point for any Java program. It must be public so the JVM can access it, static so it can be called without creating an instance of the class, and void as it doesn't return any value.",
                concise: "Program entry point: public (accessible), static (no instance), void (no return)."
            },
            {
                id: 7,
                q: "What are literals in Java?",
                a: "Literals refer to the fixed values assigned to variables. Examples: <code>100</code> (int), <code>3.14F</code> (float), <code>'A'</code> (char), <code>\"Hello\"</code> (String).",
                concise: "Fixed values assigned to variables (e.g., 100, \"Hello\")."
            },
            {
                id: 8,
                q: "What is a constructor in Java?",
                a: "A constructor is a block of code similar to a method that's called when an instance of an object is created. It has no explicit return type and has the same name as the class itself.",
                concise: "Block called on object creation; has same name as class and no return type.",
                javaCode: `public class Car {\n    String model;\n\n    // Constructor\n    public Car(String modelName) {\n        model = modelName;\n    }\n\n    public static void main(String[] args) {\n        Car myCar = new Car("Tesla");\n    }\n}`,
                pythonCode: `class Car:\n    def __init__(self, model_name):\n        self.model = model_name\n\n# Instantiate object\nmy_car = Car("Tesla")`
            },
            {
                id: 9,
                q: "Explain method overloading in Java.",
                a: "Method overloading allows a class to have more than one method with the same name if their parameter lists are different. It is an example of compile-time polymorphism.",
                concise: "Same method name, different parameters (Compile-time polymorphism)."
            },
            {
                id: 10,
                q: "What is a package in Java?",
                a: "A package is a namespace that organizes a set of related classes and interfaces, similar to different folders on your computer.",
                concise: "Namespace for organizing related classes (like folders)."
            }
        ]
    },
    {
        category: "Object-Oriented Programming (OOP)",
        questions: [
            {
                id: 11,
                q: "What is Object-Oriented Programming?",
                a: "OOP is a programming paradigm based on the concept of 'objects', which can contain data (fields) and code (methods).",
                concise: "Paradigm using 'objects' (data + code) for modular software."
            },
            {
                id: 12,
                q: "What are the main principles of OOP?",
                a: "<strong>Encapsulation:</strong> Wrapping data and methods into a single unit (class).<br/><strong>Abstraction:</strong> Hiding complex details and showing only necessary features.<br/><strong>Inheritance:</strong> Acquiring properties of a parent class.<br/><strong>Polymorphism:</strong> One thing having many forms (overloading/overriding).",
                concise: "Encapsulation, Abstraction, Inheritance, Polymorphism."
            },
            {
                id: 13,
                q: "What is inheritance?",
                a: "Inheritance is a mechanism where one object acquires all the properties and behaviors of a parent object using the <code>extends</code> keyword.",
                concise: "Mechanism to acquire properties/behaviors from a parent class (extends)."
            },
            {
                id: 14,
                q: "What is an interface?",
                a: "An interface is a reference type, similar to a class, that can contain only constants, method signatures, default/static methods, and nested types. It provides 100% abstraction (before Java 8).",
                concise: "Contract with method signatures; provides 100% abstraction."
            },
            {
                id: 15,
                q: "Difference between abstract classes and interfaces?",
                a: "<strong>Abstract Class:</strong> Can have abstract and non-abstract methods, and instance variables.<br/><strong>Interface:</strong> Primarily abstract methods (can have default/static since Java 8), no instance variables.",
                concise: "Abstract Class (State + Behavior), Interface (Behavior Contract)."
            },
            {
                id: 16,
                q: "What is polymorphism?",
                a: "Polymorphism is the ability of an object to take on many forms. Commonly seen when a parent class reference is used to refer to a child class object.",
                concise: "One thing taking many forms (e.g., parent reference to child object)."
            },
            {
                id: 17,
                q: "Explain method overriding.",
                a: "Method overriding is when a subclass provides a specific implementation of a method that is already provided by its superclass. Occurs at runtime.",
                concise: "Subclass redefines a superclass method (Runtime polymorphism)."
            },
            {
                id: 18,
                q: "What is the 'super' keyword?",
                a: "The <code>super</code> keyword is a reference variable used to refer to immediate parent class objects, methods, or constructors.",
                concise: "Reference to the immediate parent class object/method."
            },
            {
                id: 19,
                q: "What are getters and setters?",
                a: "Methods used to get and set the value of a private variable, providing a controlled way to access class members (Encapsulation).",
                concise: "Methods to access/update private fields (Encapsulation)."
            },
            {
                id: 20,
                q: "What is static in Java?",
                a: "The <code>static</code> keyword means the member belongs to the class itself rather than an instance. Static members are shared across all instances of the class.",
                concise: "Member belongs to the class, not instances (shared space).",
                javaCode: `public class Counter {\n    static int count = 0;\n\n    public Counter() {\n        count++;\n    }\n\n    public static void display() {\n        System.out.println("Static count: " + count);\n    }\n}`,
                pythonCode: `class Counter:\n    count = 0  # Class variable (static)\n\n    def __init__(self):\n        Counter.count += 1\n\n    @staticmethod\n    def display():\n        print(f"Static count: {Counter.count}")`
            }
        ]
    },
    {
        category: "Exception Handling",
        questions: [
            {
                id: 21,
                q: "What is exception handling?",
                a: "A mechanism to handle runtime errors (like IOException, SQLException) to maintain the normal flow of the application.",
                concise: "Mechanisms to handle runtime errors to keep apps running."
            },
            {
                id: 22,
                q: "What is a try-catch block?",
                a: "The <code>try</code> block contains code that might throw an exception, and the <code>catch</code> block handles that specific exception.",
                concise: "Try (monitors code), Catch (handles errors)."
            },
            {
                id: 23,
                q: "What is the finally block?",
                a: "A block used to execute important code like clean-up (closing files/connections). It executes whether an exception is handled or not.",
                concise: "Clean-up block that always executes (exception or not)."
            },
            {
                id: 24,
                q: "What is an exception?",
                a: "An exception is an event that occurs during program execution that disrupts the normal flow of instructions.",
                concise: "An unexpected event that disrupts normal execution flow."
            },
            {
                id: 25,
                q: "What are checked exceptions?",
                a: "Exceptions checked at compile-time. The programmer must handle them using try-catch or declare them using <code>throws</code> (e.g., IOException).",
                concise: "Compile-time errors; MUST be handled or declared (e.g., IOException)."
            }
            ,
            {
                id: 26,
                q: "What are unchecked exceptions?",
                a: "Exceptions not checked at compile-time. They usually occur due to programming errors (e.g., NullPointerException, ArithmeticException).",
                concise: "Run-time errors; not checked by compiler (e.g., NullPointerException)."
            },
            {
                id: 27,
                q: "What is the throws keyword?",
                a: "Used in a method signature to declare that the method might throw certain exceptions, delegating the responsibility to the caller.",
                concise: "Declares exceptions in method signature; delegates handling to caller."
            },
            {
                id: 28,
                q: "Difference between throw and throws?",
                a: "<strong>throw:</strong> Used to explicitly throw a single exception from within a method.<br/><strong>throws:</strong> Used in method signature to declare multiple exceptions.",
                concise: "throw (Inside method, 1 exception), throws (Signature, multiple)."
            },
            {
                id: 29,
                q: "What is throw used for?",
                a: "To throw an exception explicitly, often used for custom validation or re-throwing exceptions.",
                concise: "Used to explicitly trigger an exception."
            },
            {
                id: 30,
                q: "Explain custom exceptions.",
                a: "Exceptions created by the user to handle specific business logic errors, done by extending the <code>Exception</code> class.",
                concise: "User-defined errors; created by extending Exception class.",
                javaCode: `// Custom Checked Exception\nclass AgeException extends Exception {\n    public AgeException(String msg) {\n        super(msg);\n    }\n}\n\n// Usage\nif (age < 18) throw new AgeException("Too young");`,
                pythonCode: `# Custom Exception in Python\nclass AgeError(Exception):\n    pass\n\n# Usage\nif age < 18:\n    raise AgeError("Too young")`
            }
        ]
    },
    {
        category: "Core APIs",
        questions: [
            {
                id: 31,
                q: "What is the String API?",
                a: "A set of classes and methods for creating and manipulating strings (searching, comparing, extracting, etc.).",
                concise: "Classes/methods for string manipulation (search, compare, etc.)."
            },
            {
                id: 32,
                q: "Explain String, StringBuilder, and StringBuffer.",
                a: "<strong>String:</strong> Immutable.<br/><strong>StringBuilder:</strong> Mutable, not thread-safe (faster).<br/><strong>StringBuffer:</strong> Mutable, thread-safe (synchronized).",
                concise: "String (Immutable), StringBuilder (Fast), StringBuffer (Thread-safe)."
            },
            {
                id: 33,
                q: "What is an immutable object?",
                a: "An object whose state cannot be changed after it is created. Java <code>String</code> is a classic example.",
                concise: "Object whose state cannot be changed after creation."
            },
            {
                id: 34,
                q: "What are wrapper classes?",
                a: "Classes that 'wrap' primitives into objects (e.g., <code>Integer</code> for <code>int</code>). Useful for Collections which only store objects.",
                concise: "Object versions of primitives (e.g., Integer for int)."
            },
            {
                id: 35,
                q: "Explain autoboxing and unboxing.",
                a: "<strong>Autoboxing:</strong> Automatic conversion of primitive to wrapper (int -> Integer).<br/><strong>Unboxing:</strong> Reverse process (Integer -> int).",
                concise: "Auto-conversion between primitives and wrapper objects."
            },
            {
                id: 36,
                q: "What is the Collections API?",
                a: "A framework that provides architecture to store and manipulate a group of objects using interfaces (List, Set, Map) and classes (ArrayList, HashMap).",
                concise: "Framework for storing and manipulating groups of objects."
            },
            {
                id: 37,
                q: "What are generics?",
                a: "A feature that allows types to be passed as parameters to classes/methods, ensuring type safety and reducing casting.",
                concise: "Type parameters for classes/methods; ensures type safety."
            },
            {
                id: 38,
                q: "What is the Iterator interface?",
                a: "Provides methods (<code>hasNext()</code>, <code>next()</code>) to traverse any Collection and selectively remove elements.",
                concise: "Interface used to traverse and modify collections."
            },
            {
                id: 39,
                q: "Iterator vs ListIterator?",
                a: "<strong>Iterator:</strong> Forward traversal only, works for all Collections.<br/><strong>ListIterator:</strong> Bidirectional traversal, works only for Lists, can modify elements.",
                concise: "Iterator (Forward, all collections), ListIterator (Bidirectional, Lists)."
            },
            {
                id: 40,
                q: "What is a Map?",
                a: "An object that maps keys to values. Keys must be unique (e.g., HashMap, TreeMap).",
                concise: "Key-value mapping; keys must be unique."
            }
        ]
    },
    {
        category: "Concurrency",
        questions: [
            {
                id: 41,
                q: "What is concurrency?",
                a: "The ability to run multiple programs or parts of a program in parallel to maximize CPU utilization.",
                concise: "Running multiple tasks in parallel to maximize CPU."
            },
            {
                id: 42,
                q: "What is a thread?",
                a: "A lightweight sub-process, the smallest unit of processing. Java provides the <code>Thread</code> class for this.",
                concise: "Lightweight sub-process; smallest unit of execution."
            },
            {
                id: 43,
                q: "Process vs Thread?",
                a: "<strong>Process:</strong> Independent execution unit with own memory.<br/><strong>Thread:</strong> Subset of a process that shares memory and resources with other threads.",
                concise: "Process (Isolated memory), Thread (Shared memory within process)."
            },
            {
                id: 44,
                q: "What is multithreading?",
                a: "Executing multiple threads concurrently to improve the performance of an application.",
                concise: "Executing multiple threads simultaneously for performance."
            },
            {
                id: 45,
                q: "What is the synchronized keyword?",
                a: "Used to control access to shared resources by multiple threads, ensuring only one thread can access the resource at a time.",
                concise: "Ensures only one thread accesses a resource at a time."
            },
            {
                id: 46,
                q: "What is deadlock?",
                a: "A situation where two or more threads are blocked forever, waiting for each other to release resources.",
                concise: "Threads blocked forever waiting for each other's resources."
            },
            {
                id: 47,
                q: "What are thread states?",
                a: "NEW, RUNNABLE, BLOCKED, WAITING, TIMED_WAITING, TERMINATED.",
                concise: "NEW, RUNNABLE, BLOCKED, WAITING, TIMED_WAITING, TERMINATED."
            },
            {
                id: 48,
                q: "What is the volatile keyword?",
                a: "Ensures a variable's value is always read from and written to main memory, preventing thread-local caching and providing visibility across threads.",
                concise: "Bypasses thread-cache; ensures direct main memory visibility."
            },
            {
                id: 49,
                q: "What is thread safety?",
                a: "Ensuring that a shared resource can be accessed by multiple threads simultaneously without causing data inconsistency or errors.",
                concise: "Safe concurrent access to shared resources without data loss."
            },
            {
                id: 50,
                q: "How to create a thread in Java?",
                a: "1. By extending the <code>Thread</code> class.<br/>2. By implementing the <code>Runnable</code> interface (preferred for flexibility).",
                concise: "Extend Thread or Implement Runnable (Preferred)."
            },
        ]
    },
    {
        category: "Stream API",
        questions: [
            {
                id: 51,
                q: "What is the Stream API?",
                a: "A functional-style way to process sequences of elements (collections, arrays) using operations like filter, map, and reduce.",
                concise: "Functional processing of sequences (filter, map, reduce)."
            },
            {
                id: 52,
                q: "Benefits of using Streams?",
                a: "Concise, readable code, support for parallel processing, and no storage of data (lazy evaluation).",
                concise: "Concise code, parallel support, lazy evaluation."
            },
            {
                id: 53,
                q: "How to obtain a Stream from a List?",
                a: "Using the <code>list.stream()</code> method.",
                concise: "Use list.stream() method."
            },
            {
                id: 54,
                q: "map vs flatMap?",
                a: "<strong>map:</strong> One-to-one transformation (e.g., String to its length).<br/><strong>flatMap:</strong> One-to-many transformation, flattens a Stream of Streams into a single Stream.",
                concise: "map (1-to-1 transform), flatMap (1-to-many, flattens)."
            },
            {
                id: 55,
                q: "What is the filter method?",
                a: "An intermediate operation that selects elements based on a predicate (boolean condition).",
                concise: "Selects elements based on a boolean condition."
            },
            {
                id: 56,
                q: "How to sort a Stream?",
                a: "Using the <code>sorted()</code> method, which returns a stream with elements in natural or custom order.",
                concise: "Use sorted() for natural or custom ordering."
            },
            {
                id: 57,
                q: "What are terminal operations?",
                a: "Operations that trigger the stream processing and produce a final result (e.g., <code>collect()</code>, <code>forEach()</code>, <code>reduce()</code>).",
                concise: "Final operations that trigger processing (e.g., collect, forEach)."
            },
            {
                id: 58,
                q: "What is the collect method?",
                a: "A terminal operation used to transform the stream into a collection (like a List or Set).",
                concise: "Terminal op to turn stream into a collection (List/Set)."
            },
            {
                id: 59,
                q: "What is forEach?",
                a: "A terminal operation used to iterate over each element of the stream (often for printing or side effects).",
                concise: "Terminal op to iterate over each element."
            },
            {
                id: 60,
                q: "Explain the reduce operation.",
                a: "Combines all stream elements into a single result by repeatedly applying a binary operator (e.g., sum or product).",
                concise: "Combines elements into a single result (e.g., sum)."
            }
        ]
    },
    {
        category: "JDBC",
        questions: [
            {
                id: 61,
                q: "What is JDBC?",
                a: "Java Database Connectivity - an API that allows Java apps to interact with relational databases using SQL.",
                concise: "Java API to connect and interact with relational Databases."
            },
            {
                id: 62,
                q: "Core components of JDBC?",
                a: "DriverManager, Driver, Connection, Statement, ResultSet, SQLException.",
                concise: "DriverManager, Connection, Statement, ResultSet."
            },
            {
                id: 63,
                q: "How to connect to a database?",
                a: "Using <code>DriverManager.getConnection(url, user, password)</code> after loading the driver.",
                concise: "Use DriverManager.getConnection() with URL/User/Pass."
            },
            {
                id: 64,
                q: "Statement vs PreparedStatement?",
                a: "<strong>Statement:</strong> For simple SQL with no parameters.<br/><strong>PreparedStatement:</strong> For parameterized queries, prevents SQL Injection and improves performance.",
                concise: "Statement (Simple), PreparedStatement (Parameterized, Secure)."
            },
            {
                id: 65,
                q: "How to execute a query?",
                a: "Using <code>stmt.executeQuery(\"SELECT...\")</code> for fetching data or <code>stmt.executeUpdate(...)</code> for modifying data.",
                concise: "executeQuery (Fetch data), executeUpdate (Modify data)."
            },
            {
                id: 66,
                q: "What is ResultSet?",
                a: "An object representing the data returned from a SQL query, which can be traversed row by row.",
                concise: "Object representing data returned from a SQL query."
            },
            {
                id: 67,
                q: "What is batch processing?",
                a: "Executing multiple SQL statements together as a group to reduce network overhead.",
                concise: "Grouping SQL statements to execute them together."
            },
            {
                id: 68,
                q: "Types of JDBC drivers?",
                a: "Type 1 (JDBC-ODBC Bridge), Type 2 (Native-API), Type 3 (Network-Protocol), Type 4 (Thin Driver - pure Java, most common).",
                concise: "Type 1 (Bridge), 2 (Native), 3 (Net), 4 (Thin - Common)."
            },
            {
                id: 69,
                q: "How to handle SQL exceptions?",
                a: "Using try-catch blocks specifically for <code>SQLException</code> and checking error codes/SQL states.",
                concise: "Use try-catch blocks specifically for SQLException."
            },
            {
                id: 70,
                q: "What is Connection Pooling?",
                a: "Reusing database connections from a 'pool' instead of creating new ones for every request, improving performance.",
                concise: "Reusing DB connections from a pool for performance."
            }
        ]
    },
    {
        category: "List and Collections",
        questions: [
            {
                id: 71,
                q: "What is the List interface?",
                a: "An ordered collection (sequence) that allows duplicate elements and positional access via index.",
                concise: "Ordered collection that allows duplicates and index access."
            },
            {
                id: 72,
                q: "How to iterate over a List?",
                a: "Using a for-each loop, Iterator, ListIterator, or <code>list.forEach()</code> (Java 8+).",
                concise: "for-each, Iterator, ListIterator, or stream forEach."
            },
            {
                id: 73,
                q: "ArrayList vs LinkedList?",
                a: "<strong>ArrayList:</strong> Backed by an array, fast for random access, slow for middle insertions.<br/><strong>LinkedList:</strong> Doubly-linked list, fast for insertions/deletions, slow for random access.",
                concise: "ArrayList (Array-based, fast access), LinkedList (Linked-based, fast insert)."
            },
            {
                id: 74,
                q: "How to remove elements from a List?",
                a: "Using <code>remove(index)</code> or <code>remove(object)</code>. Iterators are safer for removal during traversal.",
                concise: "Use remove(index/object); Iterator is safer for traversal."
            },
            {
                id: 75,
                q: "What are Vector and Stack?",
                a: "Older, synchronized counterparts to ArrayList and Deque. Generally avoided in favor of modern non-synchronized alternatives unless thread safety is required.",
                concise: "Legacy synchronized classes; avoid in favor of ArrayList/Deque."
            },
            {
                id: 76,
                q: "How to convert List to array?",
                a: "Using <code>list.toArray(new String[0])</code> or similar.",
                concise: "Use list.toArray(new type[0]) method."
            },
            {
                id: 77,
                q: "Compare Iterator and ListIterator?",
                a: "ListIterator extends Iterator to allow bidirectional traverse and modification of elements during iteration.",
                concise: "ListIterator (Bidirectional + Modification), Iterator (Forward only)."
            },
            {
                id: 78,
                q: "What is subList method?",
                a: "Returns a 'view' of a portion of the original list between specified indices.",
                concise: "Returns a 'view' of a range within the original list."
            },
            {
                id: 79,
                q: "How to synchronize a List?",
                a: "Using <code>Collections.synchronizedList(new ArrayList<>())</code> or using <code>CopyOnWriteArrayList</code>.",
                concise: "Use Collections.synchronizedList() or CopyOnWriteArrayList."
            },
            {
                id: 80,
                q: "Benefits of Generics with List?",
                a: "Provides compile-time type safety, eliminating the need for manual type casting and reducing runtime ClassCastExceptions.",
                concise: "Compile-time type safety; no manual casting needed."
            }
        ]
    }
];
